import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useWorkshopStatus, useTestWorkshop, useSyncWorkshop, useConnectWorkshop, usePrepareState, useStartPrepare, useCheckWorkshop } from "../../hooks/useWorkshop";
import { errorMessage } from "../../hooks/useServers";

// Pantalla «Taller»: el servidor compartido donde el Orquestador construye y prueba cosas nuevas. Aquí solo se ve el estado
// (nunca contraseñas) y se prueba o se completa la conexión de los proyectos; la credencial se comparte con el candado 🔒 del chat.
export function TallerDialog({ open, onOpenChange }) {
  const { data: status, isLoading } = useWorkshopStatus(open);
  const test = useTestWorkshop();
  const sync = useSyncWorkshop();
  const connect = useConnectWorkshop();
  const { data: prep } = usePrepareState(open && !!status?.configured);
  const startPrep = useStartPrepare();
  const checkServer = useCheckWorkshop();
  const [confirmPrep, setConfirmPrep] = useState(false);
  const [prepError, setPrepError] = useState("");
  const ICON = { pending: "⚪", running: "⏳", ok: "✅", error: "❌" };

  const doPrepare = async () => {
    setPrepError("");
    setConfirmPrep(false);
    try {
      await startPrep.mutateAsync();
    } catch (e) {
      setPrepError(errorMessage(e, "No se pudo empezar la preparación."));
    }
  };
  const doCheck = async () => {
    setPrepError("");
    try {
      await checkServer.mutateAsync();
    } catch (e) {
      setPrepError(errorMessage(e, "No se pudo revisar el servidor."));
    }
  };
  const running = prep?.status === "running";
  const [host, setHost] = useState("");
  const [port, setPort] = useState("22");
  const [username, setUsername] = useState("root");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);
  const pending = (status?.projects || []).filter((p) => !p.connected);

  const submit = async () => {
    setMsg(null);
    try {
      const data = await connect.mutateAsync({ host: (status?.configured ? status.host : host).trim(), port: Number(status?.configured ? status.port : port) || 22, username: username.trim(), password });
      setMsg({ state: data.result.state, text: data.result.message });
      setPassword("");
    } catch (e) {
      setMsg({ state: "error", text: errorMessage(e, "No se pudo guardar el servidor del taller.") });
    }
  };

  const form = (
    <div data-testid="taller-form" className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2 space-y-1">
          <Label htmlFor="taller-host">IP o nombre del servidor</Label>
          <Input id="taller-host" data-testid="taller-form-host" value={status?.configured ? status.host || "" : host} onChange={(e) => setHost(e.target.value)} placeholder="Ej. 149.28.10.20" disabled={!!status?.configured} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="taller-port">Puerto</Label>
          <Input id="taller-port" data-testid="taller-form-port" value={status?.configured ? String(status.port || 22) : port} onChange={(e) => setPort(e.target.value)} disabled={!!status?.configured} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="taller-user">Usuario</Label>
          <Input id="taller-user" data-testid="taller-form-user" value={username} onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div className="col-span-2 space-y-1">
          <Label htmlFor="taller-pass">{status?.configured ? "Contraseña nueva" : "Contraseña"}</Label>
          <Input id="taller-pass" data-testid="taller-form-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
      </div>
      <Button data-testid="taller-connect-button" size="sm" onClick={submit} disabled={connect.isPending || !(status?.configured || host.trim()) || !password}>
        {connect.isPending ? "Guardando y probando..." : status?.configured ? "Cambiar contraseña y probar" : "Guardar y probar"}
      </Button>
      {msg && (
        <p data-testid="taller-connect-result" className={`text-xs ${msg.state === "success" ? "text-emerald-500" : msg.state === "warning" ? "text-amber-500" : "text-red-500"}`}>{msg.text}</p>
      )}
      <p className="text-xs text-muted-foreground">Se guarda cifrada, la IA nunca la ve, y se copia en el 09_Credenciales_y_Accesos.md de Drive.</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="taller-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Taller</DialogTitle>
          <DialogDescription>
            Un servidor aparte donde el Orquestador construye y prueba cosas nuevas por su cuenta. Cada proyecto tiene su carpeta y su usuario propios.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}

        {status && !status.configured && (
          <div data-testid="taller-not-configured" className="rounded-lg border border-amber-500/40 p-3 space-y-2">
            <p className="text-sm font-medium text-amber-500">El servidor del taller todavía no está conectado</p>
            <p className="text-xs text-muted-foreground">Captura aquí los datos del servidor (una sola vez).</p>
            {form}
          </div>
        )}

        {status?.configured && (
          <div data-testid="taller-configured" className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            <div className="rounded-lg border border-border p-3 space-y-1">
              <p className="text-sm font-medium">
                {status.last_test_ok === false ? "❌" : status.last_test_ok ? "✅" : "🟡"} Servidor conectado
              </p>
              <p className="text-xs font-mono text-muted-foreground">
                {status.username}@{status.host}:{status.port}
              </p>
              <p className="text-xs text-muted-foreground">
                {status.last_test_message || "Sin probar todavía."} {status.last_test_at ? `(${status.last_test_at})` : ""}
              </p>
            </div>

            {form}

            <div data-testid="taller-prepare" className="rounded-lg border border-border p-3 space-y-2">
              <p className="text-sm font-medium">
                Preparación del servidor {prep?.prepared ? "✅" : prep?.status === "running" ? "⏳" : prep?.status === "failed" ? "❌" : ""}
              </p>
              {!prep?.prepared && prep?.status !== "running" && (
                <p className="text-xs text-muted-foreground">
                  Falta instalar lo necesario para construir y probar cosas: git, Python, Node, yarn y un navegador para tomar capturas. Tarda de 10 a 15 minutos.
                </p>
              )}
              {prep?.steps?.length > 0 && (
                <div data-testid="taller-prepare-steps" className="space-y-0.5">
                  {prep.steps.map((st) => (
                    <p key={st.id} data-testid={`taller-step-${st.id}`} className="text-xs text-muted-foreground">
                      {ICON[st.status] || "⚪"} <span className="font-medium text-foreground">{st.title}</span>
                      {st.detail ? <span className="block pl-5 font-mono break-all">{st.detail}</span> : null}
                    </p>
                  ))}
                </div>
              )}
              {prep?.message && <p data-testid="taller-prepare-message" className={`text-xs ${prep.status === "failed" ? "text-red-500" : "text-emerald-500"}`}>{prep.message}</p>}
              {prep?.prepared && prep?.versions && Object.keys(prep.versions).length > 0 && (
                <p data-testid="taller-versions" className="text-xs text-muted-foreground font-mono break-all">
                  {Object.entries(prep.versions).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </p>
              )}
              {checkServer.data && (
                <p data-testid="taller-check-result" className="text-xs text-muted-foreground font-mono break-all">
                  {checkServer.data.os} · {checkServer.data.cpus} CPU · {checkServer.data.ram_mb} MB RAM · {checkServer.data.disk_free_gb} GB libres · swap {checkServer.data.swap_mb} MB · git {checkServer.data.git} · node {checkServer.data.node} · yarn {checkServer.data.yarn} · navegador {checkServer.data.chromium} · captura {checkServer.data.captura}
                </p>
              )}
              {prepError && <p data-testid="taller-prepare-error" className="text-xs text-red-500">{prepError}</p>}
              {confirmPrep ? (
                <div data-testid="taller-prepare-confirm" className="rounded-md border border-amber-500/40 p-2 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Esto <span className="font-medium text-foreground">instalará programas en el servidor del taller</span> (descarga de internet: nodejs.org, npm, PyPI y Playwright; unos 1.5 GB) y creará 2 GB de memoria extra. No toca tus otros servidores ni el firewall. ¿Continuar?
                  </p>
                  <div className="flex gap-2">
                    <Button data-testid="taller-prepare-yes" size="sm" onClick={doPrepare}>Sí, preparar el servidor</Button>
                    <Button data-testid="taller-prepare-no" size="sm" variant="outline" onClick={() => setConfirmPrep(false)}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button data-testid="taller-check-button" size="sm" variant="outline" onClick={doCheck} disabled={checkServer.isPending || running}>
                    {checkServer.isPending ? "Revisando..." : "Revisar (solo lectura)"}
                  </Button>
                  <Button data-testid="taller-prepare-button" size="sm" onClick={() => setConfirmPrep(true)} disabled={running || startPrep.isPending}>
                    {running ? "Preparando..." : prep?.status === "failed" ? "Continuar la preparación" : prep?.prepared ? "Volver a comprobar e instalar" : "Preparar servidor"}
                  </Button>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-medium mb-1">Proyectos del taller</p>
              {status.projects.length === 0 && (
                <p data-testid="taller-no-projects" className="text-xs text-muted-foreground">
                  Aún no hay proyectos del taller. Crea uno con «Nuevo proyecto» sin carpeta de Drive.
                </p>
              )}
              {status.projects.map((p) => (
                <p key={p.id} data-testid={`taller-project-${p.id}`} className="text-xs text-muted-foreground">
                  {p.connected ? "✅" : "🟡"} <span className="font-medium text-foreground">{p.name}</span> —{" "}
                  <span className="font-mono">{p.folder}</span> {p.connected ? "" : "(pendiente de conectar)"}
                </p>
              ))}
            </div>

            {test.data && !test.data.ok && <p data-testid="taller-test-error" className="text-xs text-red-500">{test.data.message}</p>}
            {sync.data && (
              <div data-testid="taller-sync-result" className="space-y-0.5">
                {sync.data.results.map((r, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{r.project}</span> — {r.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {status?.configured && (
            <>
              <Button data-testid="taller-test-button" variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
                {test.isPending ? "Probando..." : "Probar conexión"}
              </Button>
              {pending.length > 0 && (
                <Button data-testid="taller-sync-button" onClick={() => sync.mutate()} disabled={sync.isPending}>
                  {sync.isPending ? "Conectando..." : `Conectar ${pending.length} proyecto(s) pendiente(s)`}
                </Button>
              )}
            </>
          )}
          <Button data-testid="taller-close-button" variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
